import PropTypes from 'prop-types';
import React, { useCallback, useEffect } from 'react';
import { Link } from 'react-router-dom';

import { Download, MinimizeIcon } from 'lucide-react';
import moment from 'moment'
import { Badge, DefaultSheet, SheetBody, ButtonIcon, DefaultTable, DefaultPopover, Separator, List, ListItem } from 'twyne-new.ui';

import { formatDateTime } from 'utils';
import history from 'utils/history';

import { useIntegrationStore } from '../store';

const propTypes = {
  sweepId: PropTypes.number,
};

export default class SweepFinishedGrid extends React.Component {
  state = {
    jobs: null,
    shownJobs: [],
    statusFilter: null,
    order: 'desc',
    count: {
      delayed: 0,
      completed: 0,
      other: 0,
    },
  }

  dateField = job => job.data.cycleEndsAt && moment(job.data.cycleEndsAt)

  filterAndCount = function* (jobs) {
    const { order, statusFilter } = this.state

    const orderby = orders[order]
    jobs = jobs.sort(orderby(this.dateField))

    const count = {
      delayed: 0,
      completed: 0,
      other: 0,
    }

    for (const job of jobs) {
      switch (job.status) {
        case 'completed':
          count.completed++
          break;
        case 'delayed':
          count.delayed++
          break;
        default:
          count.other++
          break
      }

      if (!statusFilter || statusFilter === job.status || (statusFilter === 'other' && !['completed', 'delayed'].includes(job.status))) {
        yield job
      }
    }

    this.setState({ count })
  }

  filterJobsToShow = () => {
    const { jobs } = this.state

    const shownJobs = [...this.filterAndCount(jobs)]

    this.setState({ shownJobs })
  }

  handleClickPinButton = type => {
    this.setState({ statusFilter: type }, this.filterJobsToShow)
  }

  IntegrationList = ({ job }) => {
    const { integrationsRunned } = job.returnvalue || {}

    if (!integrationsRunned) {
      return null
    }

    return <>
      <DefaultPopover trigger={<ButtonIcon content='Integrations'><MinimizeIcon size='16' /></ButtonIcon>}>
        <span className='italic'>Integrations</span>
        <Separator className='m-0 my-2' />
        These are the integrations related with sweep <strong>#{job.data.sweepId}</strong>
        <List className='px-0!'>
          {integrationsRunned && Object.entries(integrationsRunned).map(([key, result]) => {
            const delivery = result === 'OK'

            return <>
              <ListItem key={`integration-${key}`} className='hover:bg-gray-100! dark:hover:bg-gray-950!'>
                <span className='twy-text-base-accent'>#{key}</span>
                <div className='flex items-center gap-2'>
                  <Badge variant='outline' color={delivery ? 'accent' : 'danger'}>
                    {delivery ? 'Delivered' : 'Failed'}
                  </Badge>
                  <this.DownloadFileButton id={key} job={job} />
                </div>
              </ListItem>
            </>
          })}
        </List>
      </DefaultPopover>
    </>
  }

  List = () => {
    const { getSweepFinished } = useIntegrationStore()
    const { id: sweepId } = this.props.match.params
    const { shownJobs, jobs } = this.state

    const loadData = useCallback(async sweepId => {
      const jobs = await getSweepFinished(sweepId)
      this.setState({ jobs }, this.filterJobsToShow)
    }, [getSweepFinished])

    useEffect(() => {
      loadData(sweepId)
    }, [loadData, sweepId])

    if (!jobs) {
      return <span className='twy-text-base-accent text-center'>Loading...</span>
    }

    const columns = [
      {
        dataField: 'job',
        text: 'Sweep',
        formatter: (_, row) => {
          const { data: { sweepId } = {} } = row
          return <Link to={`/cms/sweep/${sweepId}`}>
            <span className='hover:underline twy-text-base-accent'>
              {sweepId}
            </span>
          </Link>
        },
      }, {
        dataField: 'contest',
        text: 'Contest',
        formatter: (_, row) => {
          const { data: { contestId } = {} } = row
          return contestId
        },
      }, {
        dataField: 'endsAt',
        text: 'Ends at',
        formatter: (_, row) => {
          const date = this.dateField(row)
          return date ? formatDateTime(date) : '-'
        },
      },
      {
        dataField: 'status',
        text: 'Status',
        formatter: status => <Badge variant='light' color={colors[status]}>{labels[status] || status}</Badge>,
      },
      {
        dataField: 'integrations',
        text: '',
        formatter: (_, row) => <this.IntegrationList job={row} />,
        className: 'w-8',
      },
    ]

    return <DefaultTable
      keyField='id'
      data={shownJobs}
      columns={columns}
      noDataIndication={() => 'No jobs'}
      className='max-h-[50vh] overflow-y-auto'
      sticky
    />
  }

  DownloadFileButton = ({ id, job }) => {
    const { downloadSubscriptionFile } = useIntegrationStore()

    if (!['completed', 'delayed'].includes(job.status)) {
      return null
    }

    const handleClick = () => {
      downloadSubscriptionFile(id, job.data.contestId)
    }

    return <ButtonIcon content='Download File' onClick={handleClick}>
      <Download size={16} />
    </ButtonIcon>
  }

  PinButton = ({ children, type, color }) => {
    const { count: { [type]: count }, statusFilter } = this.state
    const active = type === statusFilter

    const handleClick = () => {
      this.handleClickPinButton(active ? null : type)
    }

    const colorConfig = {
      accent: {
        active: 'twy-subtle-accent',
        inactive: 'hover:bg-blue-50 dark:hover:bg-blue-400/10',
        border: 'twy-border-accent',
        text: 'twy-text-accent',
      },
      success: {
        active: 'twy-subtle-success',
        inactive: 'hover:bg-green-50 dark:hover:bg-green-400/10',
        border: 'twy-border-success',
        text: 'twy-text-success',
      },
      neutral: {
        active: 'bg-gray-50 dark:bg-gray-400/10 ',
        inactive: 'hover:twy-subtle-neutral',
        border: 'twy-border-neutral',
        text: 'twy-text-neutral',
      },
    }

    const colorType = colorConfig[color]

    return <>
      <div className={`${colorType[active ? 'active' : 'inactive']} border ${colorType[active ? 'active' : 'inactive']} twy-border-base py-2 px-4 w-full rounded-xs flex items-center justify-between gap-2 cursor-pointer transition-all duration-100 `} onClick={handleClick}>
        <div className={`capitalize ${colorType.text}`}>{children || type}</div>
        <Badge variant='light' size='sm' color={color}>{count}</Badge>
      </div>
    </>
  }

  render() {
    return <>
      <DefaultSheet
        title='Sweeps Finished'
        wrapperProps={{
          defaultOpen: true,
          onOpenChange: open => !open && history.replace('/settings/integrations'),
        }}
        contentProps={{ size: 'md' }}
      >
        <SheetBody className='flex flex-col gap-6'>
          <div className='flex justify-between gap-4'>
            <this.PinButton type='completed' color='success' >Finished</this.PinButton>
            <this.PinButton type='delayed' color='accent' >Active</this.PinButton>
            <this.PinButton type='other' color='neutral' />
          </div>
          <this.List />
        </SheetBody>
      </DefaultSheet>
    </>
  }
}

SweepFinishedGrid.propTypes = propTypes
SweepFinishedGrid.defaultProps = {}

const orders = {
  asc: field => (a, b) => field(a) - field(b),
  desc: field => (b, a) => field(a) - field(b),
}

const colors = {
  waiting: 'warning',
  active: 'warning',
  delayed: 'primary',
  completed: 'success',
  failed: 'danger',
}

const labels = {
  completed: 'finished',
  delayed: 'active',
}
